import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useKeycloakAuth } from "@/hooks/useKeycloakAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MaintenanceMode } from "@/components/MaintenanceMode";
import {
  MAINTENANCE_MODE,
  MAINTENANCE_TITLE,
  MAINTENANCE_DESCRIPTION,
} from "@/config/maintenance";
import { login } from "@/config/auth";
import { LogIn } from "lucide-react";
import { getLandingRouteFromToken } from "@/utils/landingRoute";

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useKeycloakAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(getLandingRouteFromToken());
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Auto-redirect to Keycloak when not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !MAINTENANCE_MODE) {
      // Pequeno delay para evitar flash
      const timer = setTimeout(() => {
        login();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authLoading, isAuthenticated]);

  const handleLogin = () => {
    login();
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Maintenance mode
  if (MAINTENANCE_MODE) {
    return (
      <MaintenanceMode
        title={MAINTENANCE_TITLE}
        description={MAINTENANCE_DESCRIPTION}
      />
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 "
      style={{ backgroundColor: "#F1EDDF" }}
    >
      <Card className="w-full max-w-md shadow-2xl backdrop-blur-sm bg-white/90 border border-white/20">
        <CardHeader className="text-center space-y-6 pb-8">
          <div>
            <h1
              className="text-3xl font-bold tracking-tight mb-2"
              style={{ color: "#10261F" }}
            >
              musa
            </h1>
          </div>
          <CardTitle
            className="text-2xl font-semibold"
            style={{ color: "#10261F" }}
          >
            Portal Musa
          </CardTitle>
          <CardDescription className="text-base text-gray-600">
            Faça login com sua conta Keycloak
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pb-8">
          <Button
            onClick={handleLogin}
            className="w-full h-12 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 focus:ring-2 focus:ring-offset-2"
            style={
              {
                backgroundColor: "#10261F",
                "--tw-ring-color": "#10261F",
              } as React.CSSProperties
            }
            size="lg"
          >
            <LogIn className="mr-2 h-5 w-5" />
            Entrar com Keycloak
          </Button>

          <p className="text-xs text-center text-gray-500">
            Você será redirecionado para a página de login segura do Keycloak
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
